import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiRequestError } from '@/lib/api';
import type { RotationEventPayload } from '@/hooks/useWebSocket';
import type { CoverageTimelineView } from '@/types/coverage-timeline';
import type { RotationForecastView } from '../missions/_components/coverageTypes';

/**
 * THE SELECTED MISSION'S ROTATION, on the COP.
 *
 * `useCopCoverage` beside this one answers "is each station manned" for the whole board, from one
 * fleet-wide read. This hook answers the two questions that only make sense about ONE mission and
 * have never been askable from the COP at all: what swap is coming, and what the rotation has been
 * doing. Those are `GET /api/coverage/:id/rotation` and `GET /api/coverage/:id/timeline`, both
 * per-mission with no batch form - so they are read for the SELECTED mission only, and for nothing
 * when there is no selection.
 *
 * SAME TWO-WRITER MODEL as `useCopCoverage` and `useCopMissionsQueue`: the socket is the mechanism,
 * the poll is the backstop for a connection that dropped without the browser noticing. The poll
 * matters here for the same reason it does there - a station can be held for an hour with no
 * transition at all, so an operator selecting a mission mid-run has no frame to learn from.
 */

/**
 * 15 s, matching `MissionCoveragePanel`'s `COVERAGE_POLL_MS` rather than `useCopCoverage`'s 20 s.
 *
 * The same endpoint answering the same question on a second surface should not drift to a
 * different backstop rate: a proposal that appears five seconds later on the COP than on the
 * mission page is a difference two operators end up having to explain to each other.
 */
const ROTATION_POLL_MS = 15_000;

/** Matches the COP's other coverage read (`useCopCoverage`), not the panel's 30 s achieved read. */
const ROTATION_REQUEST_TIMEOUT_MS = 8_000;

/**
 * How long a burst of frames is allowed to collapse into one refetch.
 *
 * A launch is not one frame. The orchestrator broadcasts a phase-only `rotation_event` at each of
 * eleven phases, and `approval` and `relief` arrive on their own frames beside them - so an
 * approved swap can produce a run of frames in a few seconds. Refetching per frame would put a
 * burst of paired requests on the API at the exact moment an aircraft is leaving a pad.
 */
const FRAME_COALESCE_MS = 400;

export interface CopRotationState {
  /** The forecast row, its history and the approval - or null when nothing has been read yet. */
  rotation: RotationForecastView | null;
  /** The Gantt, from the server. Null until a read succeeds. */
  timeline: CoverageTimelineView | null;
  /**
   * True once a read has FAILED and no newer one has succeeded.
   *
   * WITHOUT THIS, "we could not ask" is pixel-identical to "no swap is planned" - and a forecast is
   * the one reading on this surface that an operator acts on. `useCopCoverage` carries the same
   * flag for the same reason, and its comment is the argument: an operator opening the COP at 02:00
   * against a 500ing API would otherwise read an empty forecast as calm.
   *
   * A 404 IS NOT A FAILURE. It is the server saying "no continuous coverage attached to this
   * mission", which is an answer - see `readOutcome`.
   */
  degraded: boolean;
  /** When the last SUCCESSFUL read landed, so a stale surface can say how stale. Null if never. */
  lastReadAt: number | null;
  /** Force a read now - after a control is pressed, the answer should not wait for the poll. */
  refreshRotation: () => void;
}

/** A 404 from either endpoint means the coverage row is gone, not that the read failed. */
function isNotAttached(reason: unknown): boolean {
  return reason instanceof ApiRequestError && reason.status === 404;
}

export function useCopRotation(
  missionId: string | null,
  subscribeRotationEvent: (cb: (event: RotationEventPayload) => void) => () => void,
): CopRotationState {
  const [rotation, setRotation] = useState<RotationForecastView | null>(null);
  const [timeline, setTimeline] = useState<CoverageTimelineView | null>(null);
  const [degraded, setDegraded] = useState(false);
  const [lastReadAt, setLastReadAt] = useState<number | null>(null);

  /** Issue order, so a slow response cannot overwrite a newer one. */
  const seqRef = useRef(0);
  /**
   * The mission the in-flight reads were issued FOR.
   *
   * The sequence number alone is not enough. Selecting mission B while A's read is in flight and
   * then selecting A again would leave A's stale response with a sequence number that is once more
   * current - and it would be applied over B's. Both have to match.
   */
  const missionRef = useRef<string | null>(missionId);
  const coalesceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Aborted on unmount. `AbortSignal.timeout` only covers the 8 s deadline, not the teardown. */
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    const id = missionRef.current;
    if (!id) return;
    const seq = ++seqRef.current;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(ROTATION_REQUEST_TIMEOUT_MS)]);

    // Issued together and settled together: a forecast drawn against a chart from a different
    // moment is two answers to one question, and the disagreement lands on the operator.
    Promise.allSettled([
      api.get<{ data: RotationForecastView }>(`/api/coverage/${id}/rotation`, { signal }),
      api.get<{ data: CoverageTimelineView }>(`/api/coverage/${id}/timeline`, { signal }),
    ]).then(([rotationResult, timelineResult]) => {
      if (!mountedRef.current || seq !== seqRef.current || id !== missionRef.current) return;

      /**
       * A 404 IS AN ANSWER, NOT A FAILURE, and the two must not share a bucket.
       *
       * Both endpoints 404 with "No continuous coverage attached to this mission". Treating that as
       * a transport failure produced three wrong surfaces: a detach while the mission was selected
       * turned into a red "Coverage read failed - showing the last good read" over a rotation that
       * had been deliberately removed, blaming the network for an answer the server gave; coverage
       * attached by a colleague opened with that same banner over a healthy new rotation, carrying
       * a `degraded` accumulated from 404s; and the console filled with warnings from missions that
       * simply have no rotation, so a genuine 500 was indistinguishable from routine. The mission
       * panel already splits them and says why - "A 404 IS an absent forecast. It means coverage is
       * not attached at all."
       */
      const gone = (rotationResult.status === 'rejected' && isNotAttached(rotationResult.reason))
        || (timelineResult.status === 'rejected' && isNotAttached(timelineResult.reason));
      if (gone) {
        setRotation(null);
        setTimeline(null);
        setDegraded(false);
        setLastReadAt(null);
        return;
      }

      if (rotationResult.status === 'fulfilled') setRotation(rotationResult.value.data);
      if (timelineResult.status === 'fulfilled') setTimeline(timelineResult.value.data);

      // NEITHER PAYLOAD IS CLEARED ON A FAILURE. Blanking the forecast would draw "we could not
      // ask" as "nothing is coming", which is the one misreading this surface cannot afford - the
      // controls in PR 3 are gated on it. What changes is `degraded`, and the surface says so.
      if (rotationResult.status === 'rejected' || timelineResult.status === 'rejected') {
        // BOTH reasons, and the mission - the first version logged one line naming neither the
        // mission nor which endpoint failed, and swallowed the timeline error entirely when both
        // failed. A log that cannot be debugged from is a log that gets ignored.
        console.warn('Failed to load rotation for COP', {
          missionId: id,
          rotation: rotationResult.status === 'rejected' ? rotationResult.reason : null,
          timeline: timelineResult.status === 'rejected' ? timelineResult.reason : null,
        });
        setDegraded(true);
        return;
      }

      setDegraded(false);
      setLastReadAt(Date.now());
    });
  }, []);

  /**
   * The selection changed. Both payloads are dropped, and DURING RENDER rather than in an effect.
   *
   * The clearing itself is not optional - the alternative is the previous mission's swap drawn
   * under the new mission's name, and `degraded` goes with them because a failure to read mission A
   * says nothing about mission B. Doing it in an effect would clear them one commit late, which is
   * exactly that frame, painted. This is React's own "adjusting state when a prop changes" pattern:
   * the re-render happens before the browser paints anything.
   *
   * THE REFS ARE INVALIDATED HERE TOO, in the same branch, and that is not tidiness. They used to
   * be updated in a passive effect, which React schedules AFTER the commit - and a promise
   * continuation is a microtask, so an in-flight read for the old mission could settle in the gap
   * and pass both guards, because neither had been moved yet. The reset and the invalidation are
   * one operation; splitting them across the render/commit boundary is what re-opened the window
   * this hook's double-guard exists to close.
   */
  const [trackedMission, setTrackedMission] = useState<string | null>(missionId);
  if (missionId !== trackedMission) {
    setTrackedMission(missionId);
    /* eslint-disable react-hooks/refs -- Both writes are pure functions of `missionId`, run only
       inside this branch, and are idempotent, so a StrictMode double render assigns the same two
       values twice and changes nothing. The rule's usual hazard is render output that depends on a
       ref; nothing here reads them during render. Deferring them to an effect is what the
       engineering review found and rejected: an in-flight read for the old mission settles as a
       microtask, which can land after this commit but before a passive effect flushes, so both
       guards would still pass and the previous mission's forecast would be applied under the new
       mission's name - which is the exact failure the double-guard exists to prevent. */
    missionRef.current = missionId;
    seqRef.current += 1;
    /* eslint-enable react-hooks/refs */
    setRotation(null);
    setTimeline(null);
    setDegraded(false);
    setLastReadAt(null);
  }

  useEffect(() => {
    if (!missionId) return;
    refresh();
    const timer = setInterval(refresh, ROTATION_POLL_MS);
    return () => clearInterval(timer);
  }, [missionId, refresh]);

  /**
   * A frame is the TRIGGER, the endpoint is the TRUTH.
   *
   * The frame's five halves (`schedule`, `safety`, `relief`, `approval`, `phase`) are independently
   * nullable and flattened differently from the row the endpoint returns, so applying one here
   * means normalising it - a second implementation of what `MissionCoveragePanel` already does. Its
   * own record warns where that ends: the panel keeps `approval` derived on the SERVER precisely so
   * two surfaces cannot disagree about whether a swap is still approved, which is a panel showing
   * APPROVED over an aircraft the server will refuse to fly. So the frame only says "ask again",
   * and one shape reaches the render.
   */
  useEffect(() => subscribeRotationEvent((event) => {
    if (!missionRef.current || event.mission_id !== missionRef.current) return;
    if (coalesceRef.current) return;
    coalesceRef.current = setTimeout(() => {
      coalesceRef.current = null;
      refresh();
    }, FRAME_COALESCE_MS);
  }), [subscribeRotationEvent, refresh]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (coalesceRef.current) clearTimeout(coalesceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return { rotation, timeline, degraded, lastReadAt, refreshRotation: refresh };
}
